import React, { useState, useEffect } from 'react';
import { X, Search, Check } from 'lucide-react';
import { Input } from '../ui/Input';

const SingleSelectFilterModal = ({ 
  isOpen, 
  onClose, 
  onSelect,
  selectedValue = null,
  options = [],
  title = "Select Option",
  placeholder = "Select...",
  icon: Icon,
  searchPlaceholder = "Search...",
  required = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [localSelected, setLocalSelected] = useState(selectedValue);

  useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedValue);
      setSearchQuery('');
    }
  }, [isOpen, selectedValue]);

  if (!isOpen) return null;

  const filteredOptions = options.filter(opt => {
    const label = opt.label || opt.name || '';
    const value = opt.value || opt.id || '';
    const query = searchQuery.toLowerCase();
    return label.toLowerCase().includes(query) || 
           value.toLowerCase().includes(query) ||
           (opt.description && opt.description.toLowerCase().includes(query));
  });

  const handleSelect = (value) => {
    setLocalSelected(value);
    onSelect(value);
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-[60]"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl w-full max-w-sm mx-4 max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">
            {title}
            {required && <span className="text-red-500 ml-1">*</span>}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Search - only show if more than 5 options */}
        {options.length > 5 && (
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>
        )}

        {/* Options List */}
        <div className="flex-1 overflow-y-auto py-2">
          {filteredOptions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <p>No options found</p>
            </div>
          ) : (
            <div className="space-y-0">
              {filteredOptions.map((option) => {
                const optionValue = option.value || option.id || option.name;
                const isSelected = localSelected === optionValue;
                
                return (
                  <div
                    key={optionValue}
                    className={`
                      flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors
                      ${isSelected 
                        ? 'bg-blue-600 text-white' 
                        : 'hover:bg-gray-50 text-gray-700'
                      }
                    `}
                    onClick={() => handleSelect(optionValue)}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-normal">
                        {option.label || option.name}
                      </p>
                      {option.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                      )}
                      {option.offset && (
                        <p className="text-xs text-gray-500 mt-0.5">UTC{option.offset}</p>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="h-4 w-4 text-white flex-shrink-0 ml-2" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleSelectFilterModal;
